import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FormikValues, getIn } from "formik";

import { ViennaAddressSearchResult, ViennaAddressSearchResults } from "../client-file/api/ClientFileApi";
import { useClientFileApi } from "../client-file/api/ClientFileApiContext";
import { ClientFileQueryKey } from "../client-file/query/clientFileQueryKeys";
import { FormikTextField } from "../formik/FormikTextField";
import { ClientStreetAutocompleteProps } from "./ClientStreetAutocomplete.types";

const SearchDebounceMs = 300;
const MinSearchLength = 3;
const ViennaCity = "Wien";
const AustriaCountry = "Österreich";

export function ClientStreetAutocomplete<T extends FormikValues>(props: ClientStreetAutocompleteProps<T>) {
  const searchViennaAddresses = useClientFileApi().lookups?.searchViennaAddresses;

  // Without a Vienna address search the street is a plain text field (no suggestions to offer).
  if (!searchViennaAddresses) {
    return (
      <FormikTextField
        formik={props.formik}
        path={props.streetField}
        label={props.label}
        required={props.required}
        slotProps={{ input: { inputProps: { maxLength: props.maxLength } } }}
        disabled={props.disabled}
      />
    );
  }

  return <ViennaStreetAutocomplete {...props} searchViennaAddresses={searchViennaAddresses} />;
}

function ViennaStreetAutocomplete<T extends FormikValues>({
  formik,
  streetField,
  houseNumberField,
  zipCodeField,
  cityField,
  countryField,
  label,
  required,
  maxLength,
  onValueEdited,
  disabled = false,
  searchViennaAddresses,
}: ClientStreetAutocompleteProps<T> & {
  searchViennaAddresses: (params: { searchText: string }) => Promise<ViennaAddressSearchResults>;
}) {
  const { t } = useTranslation();

  const initialStreet = String(getIn(formik.initialValues, streetField) ?? "");
  const formStreet = String(getIn(formik.values, streetField) ?? "");
  const [inputValue, setInputValue] = useState(formStreet);
  const [debouncedInput, setDebouncedInput] = useState(formStreet);
  const [syncedInitial, setSyncedInitial] = useState(initialStreet);
  const [autoFilledStreet, setAutoFilledStreet] = useState<string | null>(null);

  // A form reset (new initial values after save or navigation) starts the input afresh.
  if (initialStreet !== syncedInitial) {
    setSyncedInitial(initialStreet);
    setAutoFilledStreet(null);
  }

  // The input's own edits keep the shown text and the form value in step, so a difference means
  // the street was written from outside this input (a form reset, or a bank pick copying the
  // bank's address into the form). Show that value instead of the stale text.
  if (formStreet !== inputValue) {
    setInputValue(formStreet);
    setDebouncedInput(formStreet);
    setAutoFilledStreet(null);
  }

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInput(inputValue), SearchDebounceMs);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const searchText = debouncedInput.trim();
  const isSearchEnabled = searchText.replace(/\s/g, "").length >= MinSearchLength;

  const { data, isFetching, isError } = useQuery({
    queryKey: [ClientFileQueryKey.ViennaAddresses, searchText],
    queryFn: () => searchViennaAddresses({ searchText }),
    enabled: isSearchEnabled,
    placeholderData: keepPreviousData,
  });
  const options = isSearchEnabled ? (data?.data ?? []) : [];

  const error = getIn(formik.errors, streetField) as string | undefined;
  const touched = getIn(formik.touched, streetField) as boolean | undefined;
  const showError = !!error && !!touched;

  return (
    <Autocomplete<ViennaAddressSearchResult, false, false, true>
      freeSolo
      size="small"
      fullWidth
      disabled={disabled}
      inputValue={inputValue}
      options={options}
      loading={isFetching}
      filterOptions={(x) => x}
      slotProps={{
        // Always open below the input (never flip above) and cap the height so the list scrolls.
        popper: { placement: "bottom-start", modifiers: [{ name: "flip", enabled: false }] },
        listbox: { sx: { maxHeight: 300, overflowY: "auto" } },
      }}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.street)}
      onChange={(_, newValue, reason) => {
        onValueEdited?.();
        if (reason === "clear") {
          // Clearing the address clears the whole address, not just the street.
          setInputValue("");
          setAutoFilledStreet(null);
          void formik.setFieldValue(streetField, "");
          void formik.setFieldValue(houseNumberField, "");
          void formik.setFieldValue(zipCodeField, "");
          void formik.setFieldValue(cityField, "");
          void formik.setFieldValue(countryField, "");
          return;
        }
        if (newValue && typeof newValue !== "string") {
          // A suggestion was picked — fill street, house number, postcode (PLZ), city (Ort) and country (Land).
          setInputValue(newValue.street);
          setAutoFilledStreet(newValue.street);
          void formik.setFieldValue(streetField, newValue.street);
          void formik.setFieldValue(houseNumberField, newValue.houseNumber);
          void formik.setFieldValue(zipCodeField, newValue.zipCode);
          void formik.setFieldValue(cityField, ViennaCity);
          void formik.setFieldValue(countryField, AustriaCountry);
        } else if (typeof newValue === "string") {
          // Free-text street confirmed with Enter.
          setInputValue(newValue);
          void formik.setFieldValue(streetField, newValue);
        }
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === "input") {
          onValueEdited?.();
          // Mirror typed text into the street field so free text survives without a selection.
          setInputValue(newInput);
          void formik.setFieldValue(streetField, newInput);
          // Once the street is hand-edited away from the picked suggestion, the
          // auto-filled house number / PLZ / Ort / Land are stale — drop them so
          // they aren't submitted against a different street. Pure manual entry
          // never sets autoFilledStreet, so this leaves manually typed fields alone.
          if (autoFilledStreet !== null && newInput !== autoFilledStreet) {
            setAutoFilledStreet(null);
            void formik.setFieldValue(houseNumberField, "");
            void formik.setFieldValue(zipCodeField, "");
            void formik.setFieldValue(cityField, "");
            void formik.setFieldValue(countryField, "");
          }
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={showError}
          helperText={showError ? error : " "}
          onBlur={() => void formik.setFieldTouched(streetField, true)}
          slotProps={{ input: { ...params.InputProps, inputProps: { ...params.inputProps, maxLength } } }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={`${option.street}-${option.houseNumber}-${option.zipCode}`}>
          <Stack>
            <Typography variant="body2">{`${option.street} ${option.houseNumber}`}</Typography>
            <Typography variant="caption" color="text.secondary">
              {`${option.zipCode} ${ViennaCity}`}
            </Typography>
          </Stack>
        </li>
      )}
      loadingText={<Typography variant="body2">{t("clients.create.streetSearchLoading")}</Typography>}
      noOptionsText={
        <Typography variant="body2">
          {isError
            ? t("clients.create.streetSearchError")
            : isSearchEnabled
              ? t("clients.create.streetSearchNoResults")
              : t("clients.create.streetSearchHint")}
        </Typography>
      }
    />
  );
}
